import { makeNetworkRequest } from "../utils/networkUtils";
import { chunkArray, getObjectValue, isBase64Image } from "../utils/utils";
import Axios from "axios";
import Buck from "./Buck";
import { log } from "../utils/logUtils";
import { base64ToFile } from "../utils/utils";

export default class Site {
    constructor(name, access, status, owner, slug, shared, reportPublished) {
        this.access = access;
        this.owner = owner;
        this.name = name;
        this.slug = slug;
        if(this.name && !this.slug) {
            this.slug = this.getSlug();
        } else if(this.slug && !this.name) {
            this.name = this.getSiteName();
        }
        this.status = status;
        if(reportPublished) {
            this.reportPublished = true;
        } else {
            this.reportPublished = false;
        }
        
        this.bucksObject = null;
        this.imageChunkSize = 100;
        if(shared) {
            this.shared = shared;
        } else {
            this.shared = [];
        }
        this.amountOfImages = null;
        this.buckDetections = null;
        this.location = '';
        this.acres = null;
        this.numberOfTrailCameras = null;
        this.surveyLength = '';
        this.doeDetections = null;
        this.fawnDetections = null;
        this.deerDensity = null;
        this.doeBuckRatio = null;
        this.fawnDoeRatio = null;
        this.doeEstimate = null;
        this.fawnEstimate = null;
        this.map = null;
    };

    getSiteName = () => {
        if(this.slug) {
            let name = this.slug.replaceAll("-wildlifeinvestments", "")
            name = name.replaceAll("-", " ");
            return name;
        }
    };

    getSlug = () => {
        if(this.name) {
            let slug = this.name.toLowerCase().replaceAll(" ", "-");
            return slug;
        }
    };

    getSiteStats = async (authorizationToken) => {
        let data = await makeNetworkRequest("get", `/sites/stats/${this.slug}`, authorizationToken);
        // console.log(data);

        await this.setSiteStats(data);

        return data;
    };

    setSiteStats = async (data) => {
        if(data) {
            let variablesToFind = ["amountOfImages", "buckDetections", 
                "location", "acres", "numberOfTrailCameras", "surveyLength", 
                "doeDetections", "fawnDetections", "deerDensity", "doeBuckRatio",
                "fawnDoeRatio", "doeEstimate", "fawnEstimate", "map"];
            await Promise.all(variablesToFind.map((key) => {
                if(getObjectValue(data, key)) {
                    this[key] = data[key];
                }
                return true;
            }));
        }
    };

    updateSiteStats = async (authorizationToken, updatedStats) => {
        let data = await makeNetworkRequest("post", `/sites/stats/${this.slug}`, authorizationToken, updatedStats);
        
        return data;
    };

    getSiteUnlabeledImages = async (authorizationToken, previousChunk) => {
        // The backend finds the first chunk of unlabeled images in the manifest file and sends back a list of up to 15 presigned urls to the raw images for labeling
        let data = await makeNetworkRequest("get", `/sites/images/${this.slug}/unlabeled/${previousChunk}`, authorizationToken);
        return data;
    };

    getBuckInventory = async (authorizationToken) => {
        // The backend loads the annotations file for the bucket and returns all of the bucks in the annotations file
        let data = await makeNetworkRequest("get", `/sites/bucks/${this.slug}`, authorizationToken);
        // console.log(data);
        
        if(getObjectValue(data, "bucks")) {
            let bucks = data.bucks;

            let bucksObject = {};
            await Promise.all(Object.keys(bucks).map((name) => {
                let buckObject = bucks[name];
                Object.assign(bucksObject, { [name]: new Buck(name, buckObject.images, buckObject.age, buckObject.classification, buckObject.points, buckObject.published, buckObject.notes, buckObject.privateNotes)});
                return true;
            }));
            // console.log(bucksObject);
    
            this.bucksObject = bucksObject
    
            return bucksObject;
        }
    };

    getBuck = async (authorizationToken, name) => {
        let data = await makeNetworkRequest("get", `/sites/bucks/${this.slug}/${name}`, authorizationToken);

        if(data && getObjectValue(data, 'bucks') && getObjectValue(data.bucks, name)) {
            let bucks = data.bucks;
            let buckObject = bucks[name];
    
            let bucksObject = {};
            let selectedBuck = new Buck(buckObject.name, buckObject.images, buckObject.age, buckObject.classification, buckObject.points, buckObject.published, buckObject.notes, buckObject.privateNotes);
            Object.assign(bucksObject, { [name]: selectedBuck });

            return bucksObject;
        }

        return false;
    };

    updateBuck = async (authorizationToken, buckData) => {
        let response = await makeNetworkRequest("post", `/sites/bucks/${this.slug}/${buckData.name}`, authorizationToken, buckData);
        return response;
    };

    getSiteRawImageInventory = async (authorizationToken) => {
        // The backend generates a zip file with all the raw images in the bucket and returns a presigned url for access
        let zipFileURL = await makeNetworkRequest("post", "/sites/images/raw", authorizationToken, { "resourceId": this.slug });
        return zipFileURL;
    };

    uploadImages = async (authorizationToken, imagesObject, updateManifest) => {
        let additionalImagesObject = { ...imagesObject };
        let images = Object.keys(additionalImagesObject);
        // The backend generates a presigned URL and the client uploads the images directly to S3 without occupying the backend
        let chunkedImages = await chunkArray(images, this.imageChunkSize);
        const arrayOfPromiseArrays = await Promise.all(chunkedImages.map(async (imagesChunk) => {
            // Convert list of image files to a list of dicts compatible with the backend
            let files = await Promise.all(imagesChunk.map((id) => {
                let imageObject = additionalImagesObject[id];
                let image = imageObject.file;
                let name = image.name;
                if(getObjectValue(imageObject, "name")) {
                    name = imageObject.name;
                }
                return({ "fileName": name, "fileType": image.type });
            }));
            // console.log(files);

            let response = await makeNetworkRequest("post", "/sites/upload", authorizationToken, { "resourceId": this.slug, files });
            // console.log(response);

            if (response) {
                // Upload each image using its corresponding pre-signed URL
                const uploadPromises = response.map((responseObject, index) => {
                    if(getObjectValue(responseObject, "presignedUrl")) {
                        const url = responseObject.presignedUrl;
                        const id = imagesChunk[index];
                        const file = additionalImagesObject[id].file;
                        // console.log(file);
                        const uploadConfig = {
                            headers: {
                                'Content-Type': file.type,
                            },
                            onUploadProgress: (progressEvent) => {
                                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                log(`Upload Progress for ${file.name}: ${progress}%`);
                            },
                        };
                        return Axios.put(url, file, uploadConfig);
                    } else {
                        return false;
                    }
                });
                return uploadPromises;
            } else {
                return false;
            }
        }));

        const allPromises = arrayOfPromiseArrays.flat();
        await Promise.all(allPromises);

        await Promise.all(allPromises.map((prom, index) => {
            let id = images[index];
            if(prom) {
                Object.assign(additionalImagesObject[id], { 'error': false, 'success': true });
            } else {
                Object.assign(additionalImagesObject[id], { 'error': true, 'success': false });
            }
            return true;
        }));

        if(updateManifest) {
            await makeNetworkRequest("get", `/sites/manifest/${this.slug}/update`, authorizationToken);
        }
        return additionalImagesObject;
    };

    shareWith = (email) => {
        this.shared.push(email);
    };

    stopSharingWith = (index) => {
        this.shared.splice(index, 1);
    };

    saveLabel = async (authorizationToken, label, image, imageName, originalImage) => {
        // Make sure logic to reference original image in backend if bronson and co want to keep the original image, maybe
        // provide a checkbox option for override/keep
        // If the image is a newly cropped image
        let postData = { ...label, "resourceId": this.slug, image };
        if(isBase64Image(image) && imageName && originalImage) {
            let file = await base64ToFile(image, imageName);
            let imageObject = { [image]: { file, "error": false, "success": false } };
            imageObject = await this.uploadImages(authorizationToken, imageObject, true);
            if(imageObject[image].error) {
                return false;
            }
            image = imageName;
            Object.assign(postData, { "image": imageName, originalImage });
        }

        // The backend will add the labels for the provided chunk to the manifest file in S3
        let response = await makeNetworkRequest("post", "/sites/images/label", authorizationToken, postData);
        return response;
    };

    saveResource = async (authorizationToken, create) => {
        let wasSuccessful = false;
        if(create) {
            wasSuccessful = await this.createNewResource(authorizationToken);
            this.slug = `${this.slug}-wildlifeinvestments`;
        } else {
            wasSuccessful = await this.updateResource(authorizationToken);
        }
        if(wasSuccessful && getObjectValue(wasSuccessful, "buckets")) {
            let buckets = wasSuccessful.buckets;
            if(getObjectValue(buckets, this.slug)) {
                let bucket = wasSuccessful.buckets[this.slug];
                if(getObjectValue(bucket, "permissions")) {
                    this.access = bucket.permissions;
                }
            }
        }
        return wasSuccessful;
    };

    updateResource = async (authorizationToken) => {
        let response = await makeNetworkRequest("post", "/sites/update", authorizationToken, { "resourceId": this.slug, "status": this.status, "owner": this.owner, "shared": this.shared });
        return response;
    };

    createNewResource = async (authorizationToken) => {
        let response = await makeNetworkRequest("post", "/sites/create", authorizationToken, { "name": this.name, "status": this.status, "owner": this.owner });
        return response;
    };

    deleteResource = async (authorizationToken) => {
        let response = await makeNetworkRequest("delete", `/sites/delete/${this.slug}`, authorizationToken);
        return response;
    };
};
